import type { FdicSavingsRateObservation } from "../data/fdicSavingsRateHistory";

export interface ApyRateObservation {
    timestamp: number;
    ratePct: number;
}

export interface ApyComparisonPoint {
    timestamp: number;
    usdvApyPct?: number;
    fdicSavingsRatePct?: number;
}

export interface ApyComparisonRange {
    startTimestamp: number;
    endTimestamp: number;
}

export function startOfUtcYear(timestamp: number): number {
    const date = new Date(timestamp);
    return Date.UTC(date.getUTCFullYear(), 0, 1);
}

export function toFdicRateObservations(observations: readonly FdicSavingsRateObservation[]): ApyRateObservation[] {
    return observations
        .map((observation) => ({
            timestamp: Date.parse(observation.effectiveFromUtc),
            ratePct: observation.ratePct
        }))
        .filter((observation) => Number.isFinite(observation.timestamp) && Number.isFinite(observation.ratePct))
        .sort((a, b) => a.timestamp - b.timestamp);
}

export function rateAtOrBefore(observations: readonly ApyRateObservation[], timestamp: number): number | null {
    let rate: number | null = null;

    for (const observation of observations) {
        if (observation.timestamp > timestamp) break;
        rate = observation.ratePct;
    }

    return rate;
}

/**
 * Produces one time-based chart dataset without inventing observations.
 * USDv points retain their API timestamps, while FDIC observations are seeded
 * at the visible range boundaries so the monthly step line spans the chart.
 */
export function buildApyComparisonSeries(
    usdvObservations: readonly ApyRateObservation[],
    fdicObservations: readonly ApyRateObservation[],
    range?: ApyComparisonRange
): ApyComparisonPoint[] {
    const sortedUsdv = [...usdvObservations]
        .filter((observation) => Number.isFinite(observation.timestamp) && Number.isFinite(observation.ratePct))
        .sort((a, b) => a.timestamp - b.timestamp);
    const fdic = [...fdicObservations]
        .filter((observation) => Number.isFinite(observation.timestamp) && Number.isFinite(observation.ratePct))
        .sort((a, b) => a.timestamp - b.timestamp);

    if (sortedUsdv.length === 0) return [];

    const rangeStart = range?.startTimestamp ?? sortedUsdv[0].timestamp;
    const rangeEnd = range?.endTimestamp ?? sortedUsdv[sortedUsdv.length - 1].timestamp;
    const usdv = sortedUsdv.filter(
        (observation) => observation.timestamp >= rangeStart && observation.timestamp <= rangeEnd
    );

    if (usdv.length === 0 || rangeStart > rangeEnd) return [];

    const byTimestamp = new Map<number, ApyComparisonPoint>();
    const pointAt = (timestamp: number): ApyComparisonPoint => {
        const existing = byTimestamp.get(timestamp);
        if (existing) return existing;

        const point = { timestamp };
        byTimestamp.set(timestamp, point);
        return point;
    };

    for (const observation of usdv) {
        pointAt(observation.timestamp).usdvApyPct = observation.ratePct;
    }

    if (rangeEnd > usdv[usdv.length - 1].timestamp) {
        pointAt(rangeEnd).usdvApyPct = usdv[usdv.length - 1].ratePct;
    }

    const startingFdicRate = rateAtOrBefore(fdic, rangeStart);
    if (startingFdicRate !== null) {
        pointAt(rangeStart).fdicSavingsRatePct = startingFdicRate;
    }

    for (const observation of fdic) {
        if (observation.timestamp > rangeStart && observation.timestamp <= rangeEnd) {
            pointAt(observation.timestamp).fdicSavingsRatePct = observation.ratePct;
        }
    }

    const endingFdicRate = rateAtOrBefore(fdic, rangeEnd);
    if (endingFdicRate !== null) {
        pointAt(rangeEnd).fdicSavingsRatePct = endingFdicRate;
    }

    return [...byTimestamp.values()].sort((a, b) => a.timestamp - b.timestamp);
}
